import {
  ESDialogActionButton,
  ESDialogActions,
  ESDialogContent,
  ESDialogTitle,
  ESFormDialog,
  useESSnackbar,
} from '@energy-stacks/core/ui';
import { useCloseDialogPrompt } from '@energy-stacks/shared';
import { Grid, IconButton, Stack, Typography } from '@mui/material';
import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useAppSelector } from '@energy-stacks/fleet/store';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FleetRoutes,
  weekdays,
  useNavigateBack,
} from '@energy-stacks/fleet/shared';
import {
  AddVehicleFormData,
  useCreateVehicleMutation,
  useGetCoreVehiclesQuery,
  addVehicleDataToPayload,
  RegularWorkingHours,
  vehicleTypes,
  vehicleGroups,
} from '@energy-stacks/fleet/feature-vehicles-data';
import { AddVehicleGeneralInfo } from './AddVehicleGeneralInfo';
import { AddVehicleWorkingHours } from './AddVehicleWorkingHours';
import { IconChevronLeft, IconChevronRight, IconX } from '@tabler/icons-react';
import * as yup from 'yup';
import { workingHoursValidationSchema } from '../workingHoursValidationSchema';
import { vehiclesApiErrors } from '../vehiclesApiErrors';
import { useGetPlantIdsQuery } from '@energy-stacks/fleet/feature-business-accounts-data';

type AddVehicleStep = {
  stepComponent: ReactNode;
  actionButtonType: 'reset' | 'button' | 'submit' | undefined;
  actionButtonClickHandler?: () => void;
  isActionButtonDisabled: boolean;
};

export const AddVehicle = () => {
  const [t] = useTranslation('vehicles');
  const plantId = useAppSelector((state) => state.plant.selectedPlantId);
  const { data: plantIds } = useGetPlantIdsQuery();
  const [createVehicle, { isLoading: isCreating }] = useCreateVehicleMutation();
  const { data: coreVehicles, isLoading: isFetching } =
    useGetCoreVehiclesQuery();
  const [activeStep, setActiveStep] = useState(0);
  const { showSnackbar } = useESSnackbar();

  const defaultAddVehicleValues: AddVehicleFormData = useMemo(() => {
    const startTime = new Date();
    startTime.setHours(6, 0, 0);
    const endTime = new Date();
    endTime.setHours(20, 0, 0);
    const saturdayEndTime = new Date();
    saturdayEndTime.setHours(12, 30, 0);
    return {
      plantId: { label: plantId || '', value: plantId || '' },
      name: '',
      brand: { label: '', value: '' },
      model: { label: '', value: '' },
      variant: { label: '', value: '' },
      vehicleId: '',
      licencePlate: '',
      type: {
        label: t(`vehicleTypes.${vehicleTypes[0]}`),
        value: vehicleTypes[0],
      },
      group: {
        label: t(`vehicleGroups.${vehicleGroups[0]}`),
        value: vehicleGroups[0],
      },
      hasUVFilter: false,
      workingHours: {
        regular: weekdays.reduce((acc, day) => {
          return {
            ...acc,
            [day]: {
              startTime,
              endTime,
              isWorkingDay: true,
            },
          };
        }, {} as RegularWorkingHours),
        exceptional: [],
      },
    };
  }, [plantId, t]);

  const addVehicleValidationSchema = useMemo(() => {
    return yup.object().shape({
      plantId: yup.object().shape({
        label: yup
          .string()
          .oneOf(plantIds ?? [])
          .required('vehicleFormErrors.plantIdRequired'),
        values: yup.string(),
      }),
      name: yup
        .string()
        .trim()
        .max(20, 'vehicleFormErrors.nameTooLong')
        .required('vehicleFormErrors.nameRequired'),
      brand: yup.object().shape({
        label: yup.string().required('vehicleFormErrors.brandRequired'),
        value: yup.string(),
      }),
      model: yup.object().shape({
        label: yup.string().required('vehicleFormErrors.modelRequired'),
        value: yup.string(),
      }),
      variant: yup.object().shape({
        label: yup.string().required('vehicleFormErrors.variantRequired'),
        value: yup.string(),
      }),
      vehicleId: yup
        .string()
        .trim()
        .max(30, 'vehicleFormErrors.vehicleIdTooLong')
        .required('vehicleFormErrors.vehicleIdRequired'),
      licencePlate: yup
        .string()
        .trim()
        .max(15, 'vehicleFormErrors.licencePlateTooLong'),
      type: yup.object().shape({
        label: yup.string().required('vehicleFormErrors.typeRequired'),
        value: yup.string(),
      }),
      group: yup.object().shape({
        label: yup.string().required('vehicleFormErrors.groupRequired'),
        value: yup.string(),
      }),
      hasUVFilter: yup.boolean(),
      workingHours: yup.object().shape({
        regular: yup.object().shape({
          MONDAY: workingHoursValidationSchema,
          TUESDAY: workingHoursValidationSchema,
          WEDNESDAY: workingHoursValidationSchema,
          THURSDAY: workingHoursValidationSchema,
          FRIDAY: workingHoursValidationSchema,
          SATURDAY: workingHoursValidationSchema,
          SUNDAY: workingHoursValidationSchema,
        }),
      }),
    });
  }, [plantIds]);

  const methods = useForm<AddVehicleFormData>({
    defaultValues: defaultAddVehicleValues,
    mode: 'onTouched',
    resolver: yupResolver(addVehicleValidationSchema),
  });

  const {
    formState: { isValid, isDirty, errors },
    control,
    handleSubmit,
    reset: resetForm,
  } = methods;

  const handleCloseDialog = useNavigateBack(FleetRoutes.Vehicles);
  const showExitConfirmation = useCloseDialogPrompt({
    shouldPrompt: isDirty,
    onClose: () => {
      handleCloseDialog();
      resetForm();
    },
  });

  const workingHours = useWatch({ name: 'workingHours.regular', control });
  const areAllDaysOff = useMemo(
    () => Object.values(workingHours).every((day) => !day.isWorkingDay),
    [workingHours]
  );

  const steps: AddVehicleStep[] = [
    {
      stepComponent: <AddVehicleGeneralInfo />,
      actionButtonType: 'button',
      actionButtonClickHandler: () => {
        setActiveStep((prev) => prev + 1);
      },
      isActionButtonDisabled: isFetching,
    },
    {
      stepComponent: <AddVehicleWorkingHours />,
      actionButtonType: 'submit',
      isActionButtonDisabled:
        weekdays.some(
          (day) =>
            errors.workingHours &&
            Object.keys(errors.workingHours).includes(day)
        ) ||
        !isDirty ||
        isCreating ||
        !isValid ||
        areAllDaysOff,
    },
  ];

  const onSubmit = (data: AddVehicleFormData) => {
    const coreVehicleId = coreVehicles?.find(
      (cv) =>
        cv.brandName === data.brand.value &&
        cv.model === data.model.value &&
        cv.variant === data.variant.value
    )?.vehicleCoreUuid;
    if (!coreVehicleId) return;
    createVehicle(addVehicleDataToPayload(data, coreVehicleId))
      .unwrap()
      .then(() => {
        if (plantId === data.plantId.value) {
          showSnackbar('success', 'responseMessages.successfulAdd', 'vehicles');
        } else {
          showSnackbar(
            'success',
            'responseMessages.successfulAddPlant',
            'vehicles',
            { plantId: data.plantId.value }
          );
        }
        handleCloseDialog();
      })
      .catch((error) => {
        const errorCode = vehiclesApiErrors[error.data?.errorCode];
        showSnackbar(
          'error',
          errorCode ? `responseMessages.${errorCode}` : undefined,
          'vehicles'
        );
      });
  };

  return (
    <ESFormDialog
      open
      fullWidth
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') {
          showExitConfirmation();
        }
      }}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Stack
        flexDirection="row"
        alignItems="start"
        justifyContent="space-between"
      >
        <ESDialogTitle>{t('addVehicleDialogTitle')}</ESDialogTitle>
        <IconButton
          onClick={showExitConfirmation}
          sx={{ m: 2 }}
          data-testid="addVehicleFormCloseButton"
        >
          <IconX stroke={1} />
        </IconButton>
      </Stack>
      <FormProvider {...methods}>
        <ESDialogContent
          sx={{
            borderTop: '1px solid',
            borderColor: 'grey.100',
            height: 535,
          }}
        >
          <Grid container spacing={6} sx={{ paddingY: 4 }}>
            <Grid item xs={6}>
              <Typography variant="h4" fontWeight={500} color="text.dark">
                {t(`addVehicleStepTitles.${activeStep}`)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Stack flexDirection="row" justifyContent="end">
                <Typography
                  component="span"
                  variant="inherit"
                  fontWeight={500}
                  color="secondary.main"
                  mr="5px"
                >
                  {`${t('addVehicleStepCounterLabel')}`}
                </Typography>
                <Typography
                  component="span"
                  variant="inherit"
                  fontWeight={500}
                  color="secondary.main"
                  width="20px"
                >
                  {`${activeStep + 1}/${steps.length}`}
                </Typography>
              </Stack>
            </Grid>
            {steps[activeStep].stepComponent}
          </Grid>
        </ESDialogContent>
        <ESDialogActions>
          {activeStep !== 0 && (
            <ESDialogActionButton
              variant="outlined"
              testId="addVehicleBack"
              onClick={() => {
                setActiveStep((prev) => prev - 1);
              }}
              sx={{
                mr: 'auto',
                '&.MuiButton-outlined': {
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    borderColor: 'primary.main',
                  },
                },
              }}
            >
              <IconChevronLeft style={{ marginRight: 12, height: 20 }} />
              {t('addVehicleStepBackButtonLabel')}
            </ESDialogActionButton>
          )}
          <ESDialogActionButton
            variant="contained"
            testId="addVehicleAction"
            type={steps[activeStep].actionButtonType}
            disabled={steps[activeStep].isActionButtonDisabled}
            loading={isCreating}
            onClick={(e) => {
              if (activeStep + 1 !== steps.length) e.preventDefault();
              steps[activeStep].actionButtonClickHandler?.();
            }}
          >
            {t(`addVehicleStepActionButtonLabels.${activeStep}`)}
            {activeStep + 1 !== steps.length && (
              <IconChevronRight style={{ marginLeft: 12, height: 20 }} />
            )}
          </ESDialogActionButton>
        </ESDialogActions>
      </FormProvider>
    </ESFormDialog>
  );
};
